import type { UnpausedForkStatus } from "@gearbox-protocol/deploy-tools-shared";
import { getChain } from "@gearbox-protocol/sdk/onchain";
import { QueryClient } from "@tanstack/react-query";
import { defineChain } from "viem";
import { createConfig, createStorage, http } from "wagmi";
import { injected } from "wagmi/connectors";

export const queryClient = new QueryClient();

export function buildWagmiConfig(fork: UnpausedForkStatus) {
  const { anvilChainId, originChainId, rpcUrl, alias, executionId } = fork;
  const chain = getChain(originChainId || fork.options.network);

  const anvilChain = defineChain({
    ...chain,
    id: anvilChainId,
    name: `Gearbox Testnet ${alias || executionId} (${chain.network})`,
    rpcUrls: { default: { http: [rpcUrl] } },
  });

  const storageKey = `wagmi:${alias || executionId}`;
  // wagmi's createConfig calls `store.setState(getInitialState())`
  // synchronously after constructing the persisted zustand store, which
  // fires the persist middleware and overwrites our per-fork entry in
  // localStorage with the empty initial state. That wipes the connection
  // we previously persisted whenever the fork is revisited or the page
  // is reloaded. Snapshot the entry before createConfig runs and restore
  // it immediately after, so the saved connection survives.
  const persistEntryKey = `${storageKey}.store`;
  const savedPersistEntry = window.localStorage.getItem(persistEntryKey);

  const config = createConfig({
    chains: [anvilChain],
    transports: { [anvilChainId]: http(rpcUrl) },
    connectors: [injected()],
    // Defer wagmi storage rehydrate + auto-reconnect to a post-commit
    // useEffect inside <Hydrate />. Otherwise wagmi mutates its store
    // synchronously during Hydrate's render and triggers React 19's
    // "Cannot update a component while rendering" warning on subscribers.
    ssr: true,
    // Per-fork storage namespace so connect/disconnect on one fork does
    // not overwrite the persisted wagmi state of another fork sharing
    // the same origin.
    storage: createStorage({
      key: storageKey,
      storage: window.localStorage,
    }),
  });

  if (savedPersistEntry !== null) {
    window.localStorage.setItem(persistEntryKey, savedPersistEntry);
  }

  return config;
}

/**
 * Synchronously inspect this fork's persisted wagmi store and decide
 * whether to auto-reconnect on mount.
 *
 * Without this gate, wagmi's default `reconnectOnMount = true` calls
 * `reconnect()` on every fork mount, which iterates connectors and asks
 * each `isAuthorized()`. The EIP-6963/MIPD-discovered injected connector
 * for MetaMask is `injected({ target })`, and a targeted injected
 * connector's `isAuthorized()` skips the per-fork `injected.connected`
 * storage flag and only checks `eth_accounts.length > 0`. Since
 * MetaMask grants accounts per origin (not per fork), every fork would
 * silently auto-connect once the user connected on any single fork.
 *
 * Returning `false` here makes wagmi skip `reconnect()` AND clear the
 * rehydrated connections for forks the user never connected on, while
 * still keeping the MIPD-discovered connector available so ConnectKit
 * shows MetaMask in the wallet picker when the user clicks Connect.
 */
export function shouldReconnectOnMount(fork: UnpausedForkStatus): boolean {
  try {
    const key = `wagmi:${fork.alias || fork.executionId}.store`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as {
      state?: { connections?: { value?: unknown[] } };
    };
    return (parsed.state?.connections?.value?.length ?? 0) > 0;
  } catch {
    return false;
  }
}
